/**
 * useRoutePreviewPrinting — encapsulates the route preview's print actions
 * (per dealer, per technician route, and print-all) plus printed-status tracking
 * and audit logging. Extracted from RoutePreviewPage to keep the page component
 * focused on layout/state.
 */
import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { logDataExport } from '@/lib/security/audit';
import { logUserActivity } from '@/lib/activity/userActivity';
import {
  printDealerRecallSummary,
  convertPreviewRouteToPrintRoute,
  convertPreviewDealerToPrintRoute,
  printMultipleTechnicianRoutes
} from '../utils/printRoutes';
import type { TechnicianRoute } from '../hooks/useRoutePreview';

interface PrintStatus {
  isPrinted: boolean;
  printedAt?: Date;
  printedBy?: string;
}

interface UseRoutePreviewPrintingArgs {
  dealerRoutes: TechnicianRoute[];
  visibleDealerRoutes: TechnicianRoute[];
  tenantId: string | null | undefined;
}

export function useRoutePreviewPrinting({
  dealerRoutes,
  visibleDealerRoutes,
  tenantId
}: UseRoutePreviewPrintingArgs) {
  const { toast } = useToast();
  const [printStatus, setPrintStatus] = useState<Record<string, PrintStatus>>({});
  const [isPrintingAll, setIsPrintingAll] = useState(false);

  const markPrinted = useCallback((dealerIds: string[]) => {
    setPrintStatus(prev => {
      const next = { ...prev };
      const now = new Date();
      dealerIds.forEach(id => {
        next[id] = { isPrinted: true, printedAt: now, printedBy: 'current-user' };
      });
      return next;
    });
  }, []);

  const handlePrintDealerById = useCallback(
    async (routeId: string, dealerId: string, stopNumber: number, totalStops: number) => {
      const route = dealerRoutes.find(r => r.id === routeId);
      const dealer = route?.dealers.find(d => d.id === dealerId);
      if (!route || !dealer) return;
      try {
        await printDealerRecallSummary(
          convertPreviewDealerToPrintRoute(dealer, route.technicianName)
        );
        markPrinted([dealerId]);
        logDataExport(
          'route_sheets',
          {
            action: 'print_dealer',
            dealerId,
            dealerName: dealer.name,
            stopNumber,
            totalStops,
            tenantId
          },
          dealer.stats.recalls
        );
        logUserActivity({
          category: 'printing',
          action: 'print_dealer',
          entityType: 'route_sheet',
          entityId: dealer.id,
          entityName: dealer.name,
          itemCount: dealer.stats.recalls,
          metadata: { dealer_id: dealer.id, dealer_name: dealer.name }
        });
        toast({
          title: 'Print Successful',
          description: `Route sheet for ${dealer.name} has been sent to print.`,
          variant: 'success'
        });
      } catch {
        toast({
          title: 'Print Failed',
          description: 'Failed to print dealer route sheet. Please try again.',
          variant: 'destructive'
        });
      }
    },
    [dealerRoutes, markPrinted, tenantId, toast]
  );

  const handlePrintRouteById = useCallback(
    async (routeId: string) => {
      const route = dealerRoutes.find(r => r.id === routeId);
      if (!route) return;
      try {
        await printDealerRecallSummary(convertPreviewRouteToPrintRoute(route));
        markPrinted(route.dealers.map(d => d.id));
        logDataExport(
          'route_sheets',
          {
            action: 'print_technician_route',
            technicianId: route.technicianId,
            technicianName: route.technicianName,
            dealerCount: route.dealers.length,
            recallCount: route.stats.totalRecalls,
            tenantId
          },
          route.dealers.length
        );
        logUserActivity({
          category: 'printing',
          action: 'print_technician_route',
          entityType: 'route_sheet',
          entityId: route.technicianId,
          entityName: route.technicianName,
          itemCount: route.dealers.length,
          metadata: { technician_id: route.technicianId, dealer_count: route.dealers.length }
        });
        const dealerCount = route.dealers.length;
        toast({
          title: 'Print Successful',
          description: `Route sheets for ${route.technicianName} (${dealerCount} dealer${
            dealerCount !== 1 ? 's' : ''
          }) sent to print.`,
          variant: 'success'
        });
      } catch {
        toast({
          title: 'Print Failed',
          description: `Failed to print route sheets for ${route.technicianName}. Please try again.`,
          variant: 'destructive'
        });
      }
    },
    [dealerRoutes, markPrinted, tenantId, toast]
  );

  const handlePrintAll = useCallback(async () => {
    setIsPrintingAll(true);
    try {
      if (visibleDealerRoutes.length > 0) {
        await printMultipleTechnicianRoutes(visibleDealerRoutes);
      }
      markPrinted(visibleDealerRoutes.flatMap(r => r.dealers.map(d => d.id)));
      const dealerCount = visibleDealerRoutes.reduce((s, r) => s + r.dealers.length, 0);
      logDataExport(
        'route_sheets',
        {
          action: 'print_all',
          technicianCount: visibleDealerRoutes.length,
          dealerCount,
          tenantId
        },
        dealerCount
      );
      logUserActivity({
        category: 'printing',
        action: 'print_all',
        entityType: 'route_sheet',
        itemCount: dealerCount,
        metadata: { technician_count: visibleDealerRoutes.length, dealer_count: dealerCount }
      });
      const techCount = visibleDealerRoutes.length;
      toast({
        title: 'Print All Successful',
        description: `All route sheets for ${techCount} technician${
          techCount !== 1 ? 's' : ''
        } have been sent to print.`,
        variant: 'success'
      });
    } catch {
      toast({
        title: 'Print Failed',
        description: 'Failed to print all route sheets. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsPrintingAll(false);
    }
  }, [visibleDealerRoutes, markPrinted, tenantId, toast]);

  const allPrinted = useMemo(() => {
    const visibleDealers = visibleDealerRoutes.flatMap(r => r.dealers);
    return visibleDealers.length > 0 && visibleDealers.every(d => printStatus[d.id]?.isPrinted);
  }, [visibleDealerRoutes, printStatus]);

  return {
    printStatus,
    isPrintingAll,
    allPrinted,
    handlePrintDealerById,
    handlePrintRouteById,
    handlePrintAll
  };
}

export default useRoutePreviewPrinting;
